import { Form as BootstrapForm, Row, Col } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import Select from "react-select";
import {
  OpenRouterKeyFormState,
  KeyType,
  KEY_TYPE_OPTIONS,
  INITIAL_FORM_STATE,
  LLM_OPTIONS,
} from "../types";

interface OptionType {
  value: string | number;
  label: string;
}

interface OpenRouterKeyFormProps {
  formState: OpenRouterKeyFormState;
  setFormState: (state: OpenRouterKeyFormState) => void;
  editing: boolean;
}

const OpenRouterKeyForm = ({
  formState,
  setFormState,
  editing,
}: OpenRouterKeyFormProps): JSX.Element => {
  const selectedKeyType = KEY_TYPE_OPTIONS.find(
    (opt) => opt.value === formState.keyType
  );

  const handleKeyTypeChange = (e: unknown) => {
    const option = e as OptionType | null;
    if (!option) return;
    const newKeyType = option.value as KeyType;
    setFormState({
      ...INITIAL_FORM_STATE,
      id: formState.id,
      name: formState.name,
      llms: formState.llms,
      keyType: newKeyType,
    });
  };

  const updateField = (field: keyof OpenRouterKeyFormState, value: string) => {
    setFormState({ ...formState, [field]: value });
  };

  const renderAzureFields = () => (
    <>
      <Row>
        <Col md={6}>
          <TextField
            label="Model Slug"
            id="modelSlug"
            value={formState.modelSlug}
            onChange={(e) => updateField("modelSlug", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Endpoint URL"
            id="endpointUrl"
            value={formState.endpointUrl}
            onChange={(e) => updateField("endpointUrl", e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="API Key"
            id="apiKey"
            value={formState.apiKey}
            onChange={(e) => updateField("apiKey", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Model ID"
            id="modelId"
            value={formState.modelId}
            onChange={(e) => updateField("modelId", e.target.value)}
            required
          />
        </Col>
      </Row>
    </>
  );

  const renderAwsBedrockFields = () => (
    <>
      <Row>
        <Col md={6}>
          <TextField
            label="Key"
            id="key"
            value={formState.key}
            onChange={(e) => updateField("key", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Access Key ID"
            id="accessKeyId"
            value={formState.accessKeyId}
            onChange={(e) => updateField("accessKeyId", e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="Secret Access Key"
            id="secretAccessKey"
            value={formState.secretAccessKey}
            onChange={(e) => updateField("secretAccessKey", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Region"
            id="region"
            value={formState.region}
            onChange={(e) => updateField("region", e.target.value)}
            required
          />
        </Col>
      </Row>
    </>
  );

  const renderGoogleVertexFields = () => (
    <>
      <Row>
        <Col md={6}>
          <TextField
            label="Model Type"
            id="modelType"
            value={formState.modelType}
            onChange={(e) => updateField("modelType", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Project ID"
            id="projectId"
            value={formState.projectId}
            onChange={(e) => updateField("projectId", e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="Private Key ID"
            id="privateKeyId"
            value={formState.privateKeyId}
            onChange={(e) => updateField("privateKeyId", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Private Key"
            id="privateKey"
            value={formState.privateKey}
            onChange={(e) => updateField("privateKey", e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="Client Email"
            id="clientEmail"
            value={formState.clientEmail}
            onChange={(e) => updateField("clientEmail", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Client ID"
            id="clientId"
            value={formState.clientId}
            onChange={(e) => updateField("clientId", e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="Auth URI"
            id="authUri"
            value={formState.authUri}
            onChange={(e) => updateField("authUri", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Token URI"
            id="tokenUri"
            value={formState.tokenUri}
            onChange={(e) => updateField("tokenUri", e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="Auth Provider X509 Cert URL"
            id="authProviderX509CertUrl"
            value={formState.authProviderX509CertUrl}
            onChange={(e) =>
              updateField("authProviderX509CertUrl", e.target.value)
            }
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Client X509 Cert URL"
            id="clientX509CertUrl"
            value={formState.clientX509CertUrl}
            onChange={(e) => updateField("clientX509CertUrl", e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label="Universe Domain"
            id="universeDomain"
            value={formState.universeDomain}
            onChange={(e) => updateField("universeDomain", e.target.value)}
            required
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Region"
            id="region"
            value={formState.region}
            onChange={(e) => updateField("region", e.target.value)}
            required
          />
        </Col>
      </Row>
    </>
  );

  const renderOtherFields = () => (
    <Row>
      <Col md={6}>
        <TextField
          label="Key"
          id="key"
          value={formState.key}
          onChange={(e) => updateField("key", e.target.value)}
          required
        />
      </Col>
    </Row>
  );

  const renderFieldsForKeyType = () => {
    switch (formState.keyType) {
      case "azure":
        return renderAzureFields();
      case "aws_bedrock":
        return renderAwsBedrockFields();
      case "google_vertex":
        return renderGoogleVertexFields();
      case "other":
        return renderOtherFields();
      default:
        return null;
    }
  };

  return (
    <BootstrapForm className="openRouterKeyForm">
      <Row>
        <Col md={5}>
          <TextField
            label="Name"
            id="name"
            value={formState.name}
            onChange={(e) => updateField("name", e.target.value)}
            required
          />
        </Col>
        <Col md={5}>
          <RequiredReactSelect
            label="Key Type"
            options={KEY_TYPE_OPTIONS}
            value={selectedKeyType || undefined}
            onChange={handleKeyTypeChange}
            placeholder="Select a key type..."
            required
            isDisabled={editing}
          />
        </Col>
        <Col
          md={2}
          style={{ display: "flex", alignItems: "center", paddingTop: "16px" }}
        >
          <BootstrapForm.Check
            type="checkbox"
            id="default"
            label="Default"
            checked={formState.default}
            onChange={(e) =>
              setFormState({ ...formState, default: e.target.checked })
            }
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <BootstrapForm.Group className="formGroup">
            <BootstrapForm.Label>
              Which language models would you prefer to use with this key?
            </BootstrapForm.Label>
            <Select
              isMulti
              options={LLM_OPTIONS}
              value={formState.llms.map((llm) => ({
                value: llm,
                label: llm,
              }))}
              onChange={(selected) => {
                setFormState({
                  ...formState,
                  llms: selected ? selected.map((s) => s.value) : [],
                });
              }}
              placeholder="Search and select LLMs..."
            />
          </BootstrapForm.Group>
        </Col>
      </Row>
      {renderFieldsForKeyType()}
    </BootstrapForm>
  );
};

export default OpenRouterKeyForm;
